import { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../auth";
import { useNavigate } from "react-router-dom";
import OxyHeader from "../components/OxyHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Settings, Users, Handshake, Store as StoreIcon, TrendingUp, Trash2, DollarSign, Wallet, MessageCircle, User, Bell } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [oxySettings, setOxySettings] = useState(null);
  const [ecomSettings, setEcomSettings] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [plans, setPlans] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [payments, setPayments] = useState([]);
  const [showcase, setShowcase] = useState([]);
  const [users, setUsers] = useState([]);
  const [gateways, setGateways] = useState({});
  const [builders, setBuilders] = useState([]);
  const [pendingCounts, setPendingCounts] = useState({payments_pending: 0, orders_pending: 0, tickets_open: 0, withdrawals_pending: 0});
  const founderPhotoRef = useRef(null);
  const [newShowcase, setNewShowcase] = useState({ title: "", category: "business", url: "", image_url: "", description: "", featured: false, visible: true });

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    if (user.role !== "super_admin") { navigate("/dashboard"); return; }
    api.get("/analytics/platform").then(r => setAnalytics(r.data));
    api.get("/oxy/settings").then(r => setOxySettings(r.data));
    api.get("/settings").then(r => setEcomSettings(r.data));
    api.get("/websites/all").then(r => setWebsites(r.data));
    api.get("/plans").then(r => setPlans(r.data));
    api.get("/affiliate/all").then(r => setAffiliates(r.data));
    api.get("/affiliate/withdrawals").then(r => setWithdrawals(r.data));
    api.get("/support/tickets").then(r => setTickets(r.data));
    api.get("/payments/all").then(r => setPayments(r.data));
    api.get("/showcase/all").then(r => setShowcase(r.data)).catch(() => {});
    api.get("/admin/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/admin/gateways").then(r => setGateways(r.data)).catch(() => {});
    api.get("/builders/all").then(r => setBuilders(r.data)).catch(() => {});
    api.get("/admin/overview").then(r => setAnalytics(prev => ({...(prev||{}), ...r.data}))).catch(() => {});
    const fetchPending = () => api.get("/admin/pending-counts").then(r => setPendingCounts(r.data)).catch(() => {});
    fetchPending();
    const t = setInterval(fetchPending, 15000);
    return () => clearInterval(t);
  }, [user]);

  if (!user || user.role !== "super_admin") return null;

  const saveOxy = async () => { await api.put("/oxy/settings", oxySettings); toast.success("Saved"); };
  const saveEcom = async () => { await api.put("/settings", ecomSettings); toast.success("Saved"); };
  const savePlan = async (p) => { await api.put(`/plans/${p.plan_id}`, p); toast.success("Plan saved"); };
  const setTicket = async (id, status) => { await api.put(`/support/ticket/${id}`, { status }); setTickets(tickets.map(t => t.ticket_id === id ? { ...t, status } : t)); };

  const verifyPayment = async (session_id, verified) => {
    await api.put(`/payments/${session_id}/verify`, { verified });
    api.get("/payments/all").then(r => setPayments(r.data));
    toast.success(verified ? "Verified & demo unlocked" : "Rejected");
  };

  const setWallet = (i, patch) => {
    const wallets = [...(oxySettings.crypto_wallets || [])];
    wallets[i] = { ...wallets[i], ...patch };
    setOxySettings({ ...oxySettings, crypto_wallets: wallets });
  };
  const addWallet = () => setOxySettings({ ...oxySettings, crypto_wallets: [...(oxySettings.crypto_wallets || []), { network: "NEW", address: "", note: "" }] });
  const delWallet = (i) => setOxySettings({ ...oxySettings, crypto_wallets: oxySettings.crypto_wallets.filter((_, idx) => idx !== i) });

  const addShowcase = async () => {
    if (!newShowcase.title) return toast.error("Title required");
    const r = await api.post("/showcase", newShowcase);
    setShowcase([...showcase, r.data]);
    setNewShowcase({ title: "", category: "business", url: "", image_url: "", description: "", featured: false, visible: true });
    toast.success("Added");
  };
  const delShowcase = async (id) => { await api.delete(`/showcase/${id}`); setShowcase(showcase.filter(s => s.showcase_id !== id)); };
  const toggleShowcase = async (id, key, val) => { await api.put(`/showcase/${id}`, { [key]: val }); setShowcase(showcase.map(s => s.showcase_id === id ? {...s, [key]: val} : s)); };

  const changeRole = async (uid, role) => { const r = await api.put(`/admin/users/${uid}/role`, { role }); setUsers(users.map(u => u.user_id === uid ? r.data : u)); toast.success("Role updated"); };
  const changeStatus = async (uid, status) => { await api.put(`/admin/users/${uid}/status`, { status }); setUsers(users.map(u => u.user_id === uid ? { ...u, status } : u)); toast.success(`User ${status}`); };
  const removeUser = async (uid) => { if (!window.confirm("Delete user?")) return; await api.delete(`/admin/users/${uid}`); setUsers(users.filter(u => u.user_id !== uid)); };

  const saveGateway = async () => { const r = await api.put("/admin/gateways", gateways); setGateways(r.data); toast.success("Gateways saved"); };
  const setGw = (k, patch) => setGateways({ ...gateways, [k]: { ...(gateways[k] || {}), ...patch } });

  const toggleBuilder = async (id, enabled) => {
    const r = await api.put(`/builders/${id}`, { enabled });
    setBuilders(builders.map(b => b.id === id ? r.data : b));
    toast.success(`${r.data.name} ${enabled ? "enabled" : "disabled"}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <OxyHeader/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-extrabold mb-1">Super Admin</h1>
            <p className="text-slate-500 text-sm font-bn">সম্পূর্ণ প্ল্যাটফর্ম নিয়ন্ত্রণ</p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="pending-strip">
            <PendingBadge label="Payments" count={pendingCounts.payments_pending} color="rose" testid="pending-payments"/>
            <PendingBadge label="Orders" count={pendingCounts.orders_pending} color="amber" testid="pending-orders"/>
            <PendingBadge label="Tickets" count={pendingCounts.tickets_open} color="sky" testid="pending-tickets"/>
            <PendingBadge label="Withdrawals" count={pendingCounts.withdrawals_pending} color="violet" testid="pending-withdrawals"/>
          </div>
        </div>

        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            {[
              ["Websites", websites.length],
              ["Products", analytics.products],
              ["Orders", analytics.orders],
              ["GMV", `৳${analytics.revenue.toLocaleString()}`],
              ["Commission", `৳${analytics.commission.toLocaleString()}`],
            ].map(([l, v], i) => (
              <div key={i} className="p-4 bg-white rounded-xl border">
                <div className="text-xs text-slate-500 uppercase font-semibold">{l}</div>
                <div className="text-xl font-black font-mono mt-1">{v}</div>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="brand">
          <TabsList className="bg-white border mb-6 flex-wrap h-auto">
            <TabsTrigger value="brand" data-testid="atab-brand"><Settings className="w-4 h-4 mr-1.5"/>Brand & Settings</TabsTrigger>
            <TabsTrigger value="builders" data-testid="atab-builders">🧱 Builders</TabsTrigger>
            <TabsTrigger value="users" data-testid="atab-users">👤 Users</TabsTrigger>
            <TabsTrigger value="gateways" data-testid="atab-gateways">🏦 Gateways</TabsTrigger>
            <TabsTrigger value="showcase" data-testid="atab-showcase">🌟 Showcase</TabsTrigger>
            <TabsTrigger value="plans" data-testid="atab-plans"><DollarSign className="w-4 h-4 mr-1.5"/>Plans</TabsTrigger>
            <TabsTrigger value="tenants" data-testid="atab-tenants"><StoreIcon className="w-4 h-4 mr-1.5"/>Websites</TabsTrigger>
            <TabsTrigger value="affiliates" data-testid="atab-affiliates"><Handshake className="w-4 h-4 mr-1.5"/>Affiliates</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="atab-withdrawals"><Wallet className="w-4 h-4 mr-1.5"/>Withdrawals</TabsTrigger>
            <TabsTrigger value="tickets" data-testid="atab-tickets"><MessageCircle className="w-4 h-4 mr-1.5"/>Support</TabsTrigger>
            <TabsTrigger value="payments" data-testid="atab-payments"><DollarSign className="w-4 h-4 mr-1.5"/>Payments</TabsTrigger>
            <TabsTrigger value="crypto" data-testid="atab-crypto">₿ Crypto Wallets</TabsTrigger>
            <TabsTrigger value="ecom" data-testid="atab-ecom">E-commerce</TabsTrigger>
          </TabsList>

          <TabsContent value="brand">
            {oxySettings && (
              <div className="max-w-3xl bg-white p-6 rounded-xl border space-y-4">
                <h3 className="font-bold text-lg">Platform Branding</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Brand name</Label><Input value={oxySettings.brand_name} onChange={e => setOxySettings({...oxySettings, brand_name: e.target.value})}/></div>
                  <div><Label>Primary color</Label><Input type="color" value={oxySettings.primary_color} onChange={e => setOxySettings({...oxySettings, primary_color: e.target.value})} className="h-10"/></div>
                  <div><Label>WhatsApp number</Label><Input value={oxySettings.whatsapp_number} onChange={e => setOxySettings({...oxySettings, whatsapp_number: e.target.value})} data-testid="admin-whatsapp"/></div>
                  <div><Label>Support email</Label><Input value={oxySettings.support_email} onChange={e => setOxySettings({...oxySettings, support_email: e.target.value})}/></div>
                  <div><Label>bKash Personal Number</Label><Input value={oxySettings.bkash_number || ""} onChange={e => setOxySettings({...oxySettings, bkash_number: e.target.value})} data-testid="admin-bkash-num" placeholder="01xxxxxxxxx"/></div>
                  <div><Label>Nagad Personal Number</Label><Input value={oxySettings.nagad_number || ""} onChange={e => setOxySettings({...oxySettings, nagad_number: e.target.value})} data-testid="admin-nagad-num" placeholder="01xxxxxxxxx"/></div>
                  <div><Label>Demo fee ৳ BDT</Label><Input type="number" value={oxySettings.demo_fee_bd} onChange={e => setOxySettings({...oxySettings, demo_fee_bd: parseFloat(e.target.value) || 0})}/></div>
                  <div><Label>Demo fee $ USD</Label><Input type="number" value={oxySettings.demo_fee_intl} onChange={e => setOxySettings({...oxySettings, demo_fee_intl: parseFloat(e.target.value) || 0})}/></div>
                  <div><Label>Hosting subdomain</Label><Input value={oxySettings.hosting_subdomain} onChange={e => setOxySettings({...oxySettings, hosting_subdomain: e.target.value})}/></div>
                  <div><Label>Affiliate commission %</Label><Input type="number" value={oxySettings.affiliate_default_percent} onChange={e => setOxySettings({...oxySettings, affiliate_default_percent: parseFloat(e.target.value) || 0})}/></div>
                </div>
                <div><Label>Tagline (বাংলা)</Label><Input value={oxySettings.tagline_bn} onChange={e => setOxySettings({...oxySettings, tagline_bn: e.target.value})} className="font-bn"/></div>
                <div><Label>Tagline (English)</Label><Input value={oxySettings.tagline_en} onChange={e => setOxySettings({...oxySettings, tagline_en: e.target.value})}/></div>

                <h3 className="font-bold text-lg pt-4 border-t">Platform Logo</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Light logo (URL or upload path)</Label>
                    <Input value={oxySettings.platform_logo_url || ""} onChange={e => setOxySettings({...oxySettings, platform_logo_url: e.target.value})} placeholder="uploads/logo.png or full URL" data-testid="admin-logo"/>
                    <p className="text-xs text-slate-500 mt-1">Upload via Website Editor → Content → Logo, then paste path here. Empty = text logo.</p>
                  </div>
                  <div>
                    <Label>Dark-mode logo</Label>
                    <Input value={oxySettings.platform_logo_dark_url || ""} onChange={e => setOxySettings({...oxySettings, platform_logo_dark_url: e.target.value})} placeholder="uploads/logo-dark.png"/>
                  </div>
                </div>

                <h3 className="font-bold text-lg pt-4 border-t"><User className="w-4 h-4 inline mr-1"/>Founder</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={oxySettings.founder_name} onChange={e => setOxySettings({...oxySettings, founder_name: e.target.value})} data-testid="founder-name"/></div>
                  <div><Label>Designation</Label><Input value={oxySettings.founder_designation} onChange={e => setOxySettings({...oxySettings, founder_designation: e.target.value})}/></div>
                </div>
                <div><Label>Bio</Label><Textarea value={oxySettings.founder_bio} onChange={e => setOxySettings({...oxySettings, founder_bio: e.target.value})} rows={3}/></div>
                <div>
                  <Label>Founder Photo</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {oxySettings.founder_photo ? (
                      <img src={fileUrl(oxySettings.founder_photo)} alt="Founder" className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"/>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 border-2 border-dashed border-slate-300"><User className="w-8 h-8"/></div>
                    )}
                    <div className="flex-1 space-y-2">
                      <input ref={founderPhotoRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const fd = new FormData(); fd.append("file", f);
                        const r = await api.post("/upload", fd, { headers: {"Content-Type": "multipart/form-data"} });
                        setOxySettings({...oxySettings, founder_photo: r.data.path});
                        toast.success("Founder photo uploaded — click Save to persist");
                      }}/>
                      <Button size="sm" onClick={() => founderPhotoRef.current?.click()} variant="outline" data-testid="upload-founder-photo">Upload photo</Button>
                      {oxySettings.founder_photo && <Button size="sm" variant="outline" onClick={() => setOxySettings({...oxySettings, founder_photo: ""})} className="text-rose-500 border-rose-200 ml-2">Remove</Button>}
                      <div className="text-[11px] text-slate-500">Or paste URL/path: <Input value={oxySettings.founder_photo || ""} onChange={e => setOxySettings({...oxySettings, founder_photo: e.target.value})} className="mt-1 h-8 text-xs" placeholder="uploads/founder.jpg or https://..."/></div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-sky-50 border border-sky-100">
                  <div><div className="font-semibold text-sm">AI Assistant enabled</div></div>
                  <Switch checked={oxySettings.ai_enabled} onCheckedChange={v => setOxySettings({...oxySettings, ai_enabled: v})}/>
                </div>
                <Button onClick={saveOxy} className="bg-sky-500 hover:bg-sky-600" data-testid="save-oxy">Save Platform Settings</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="builders">
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">SaaS Builders</h3>
                  <p className="text-xs text-slate-500">Toggle which builders are publicly visible on the landing page. Each has its own route.</p>
                </div>
                <div className="text-xs text-slate-500">Enabled: {builders.filter(b => b.enabled).length}/{builders.length}</div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {builders.map(b => (
                  <div key={b.id} className={`p-4 rounded-lg border-2 ${b.enabled ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50 opacity-70"}`} data-testid={`builder-${b.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-bold">{b.name}</div>
                        <div className="text-xs text-slate-500 font-bn">{b.name_bn}</div>
                        <div className="mt-1 text-[10px] font-mono text-sky-600">{b.route}</div>
                      </div>
                      <Switch checked={!!b.enabled} onCheckedChange={v => toggleBuilder(b.id, v)} data-testid={`toggle-${b.id}`}/>
                    </div>
                    <div className="mt-2 text-xs text-slate-600 font-bn">{b.tagline_bn}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="bg-white rounded-xl border">
              <div className="p-4 border-b flex flex-wrap gap-2 items-center">
                <h3 className="font-bold text-lg">All Users ({users.length})</h3>
                <div className="flex flex-wrap gap-2 ml-auto">
                  {["super_admin","store_owner","affiliate","customer"].map(r => (
                    <span key={r} className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-slate-100 text-slate-600">{r}: {users.filter(u => u.role === r).length}</span>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2">User</th><th className="text-left px-4 py-2">Role</th><th className="text-left px-4 py-2">Status</th><th className="text-right px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id} className="border-t" data-testid={`user-${u.user_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {u.picture ? <img src={u.picture} className="w-8 h-8 rounded-full"/> : <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold">{(u.name||u.email||"?").charAt(0).toUpperCase()}</div>}
                            <div><div className="font-semibold text-sm">{u.name || u.email}</div><div className="text-xs text-slate-500">{u.email}</div></div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <select value={u.role} onChange={e => changeRole(u.user_id, e.target.value)} className="text-xs rounded border px-2 py-1" data-testid={`role-${u.user_id}`}>
                            {["customer","store_owner","affiliate","super_admin"].map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${u.status === "banned" ? "bg-rose-100 text-rose-700" : u.status === "suspended" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{u.status || "active"}</span>
                        </td>
                        <td className="px-4 py-3 text-right space-x-1">
                          {u.status !== "suspended" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => changeStatus(u.user_id, "suspended")}>Suspend</Button>}
                          {u.status === "suspended" && <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600" onClick={() => changeStatus(u.user_id, "active")}>Activate</Button>}
                          <Button size="sm" variant="outline" onClick={() => removeUser(u.user_id)} className="h-7 text-xs text-rose-500 border-rose-200"><Trash2 className="w-3 h-3"/></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="gateways">
            <div className="max-w-3xl bg-white p-6 rounded-xl border space-y-5" data-testid="gateway-panel">
              <h3 className="font-bold text-lg">Payment Gateway Configuration</h3>
              {["bkash","nagad"].map(k => (
                <div key={k} className="p-4 rounded-lg border bg-slate-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold uppercase text-sm">{k}</span>
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={gateways[k]?.enabled !== false} onChange={e => setGw(k, {enabled: e.target.checked})}/>Enabled</label>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div><Label className="text-xs">Merchant number</Label><Input value={gateways[k]?.merchant_number || ""} onChange={e => setGw(k, {merchant_number: e.target.value})} placeholder="01888496974" data-testid={`gw-${k}-num`}/></div>
                    <div><Label className="text-xs">Instructions</Label><Input value={gateways[k]?.instructions || ""} onChange={e => setGw(k, {instructions: e.target.value})}/></div>
                  </div>
                </div>
              ))}
              <div className="p-4 rounded-lg border bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold uppercase text-sm">Bank Transfer</span>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={gateways.bank?.enabled === true} onChange={e => setGw("bank", {enabled: e.target.checked})}/>Enabled</label>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <div><Label className="text-xs">Bank name</Label><Input value={gateways.bank?.bank_name || ""} onChange={e => setGw("bank", {bank_name: e.target.value})}/></div>
                  <div><Label className="text-xs">Account name</Label><Input value={gateways.bank?.account_name || ""} onChange={e => setGw("bank", {account_name: e.target.value})}/></div>
                  <div><Label className="text-xs">Account number</Label><Input value={gateways.bank?.account_number || ""} onChange={e => setGw("bank", {account_number: e.target.value})}/></div>
                  <div><Label className="text-xs">Routing / branch</Label><Input value={gateways.bank?.routing || ""} onChange={e => setGw("bank", {routing: e.target.value})}/></div>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-slate-900 text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold uppercase text-sm">Stripe</span>
                  <span className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-300">{gateways.stripe?.mode || "test"} mode</span>
                </div>
                <div className="text-xs text-slate-400">Publishable key: <span className="font-mono">{gateways.stripe?.publishable_key_display}</span> · Secret in .env only</div>
              </div>
              <Button onClick={saveGateway} className="bg-sky-500 hover:bg-sky-600" data-testid="save-gateways">Save Gateway Config</Button>
            </div>
          </TabsContent>

          <TabsContent value="showcase">
            <div className="max-w-4xl bg-white p-6 rounded-xl border space-y-5">
              <h3 className="font-bold text-lg">Successful Projects Showcase</h3>
              <p className="text-xs text-slate-500 -mt-2">Landing page-এ দেখানোর জন্য সফল প্রজেক্ট যোগ করুন। প্রতিটির screenshot URL দিন।</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <div><Label className="text-xs">Title</Label><Input value={newShowcase.title} onChange={e => setNewShowcase({...newShowcase, title: e.target.value})} data-testid="sc-title"/></div>
                <div><Label className="text-xs">Category</Label>
                  <select value={newShowcase.category} onChange={e => setNewShowcase({...newShowcase, category: e.target.value})} className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm">
                    {["ecommerce","business","news","blog","portfolio","restaurant","school","agency","ngo","personal","service","landing"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><Label className="text-xs">Live URL</Label><Input value={newShowcase.url} onChange={e => setNewShowcase({...newShowcase, url: e.target.value})} placeholder="https://..."/></div>
                <div><Label className="text-xs">Screenshot URL</Label><Input value={newShowcase.image_url} onChange={e => setNewShowcase({...newShowcase, image_url: e.target.value})} placeholder="https://..."/></div>
                <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Input value={newShowcase.description} onChange={e => setNewShowcase({...newShowcase, description: e.target.value})} className="font-bn"/></div>
              </div>
              <Button onClick={addShowcase} className="bg-emerald-500 hover:bg-emerald-600" data-testid="sc-add">+ Add showcase</Button>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                {showcase.map(s => (
                  <div key={s.showcase_id} className="border rounded-lg p-3 flex gap-3" data-testid={`sc-item-${s.showcase_id}`}>
                    <div className="w-24 h-16 bg-slate-100 bg-cover bg-center rounded flex-shrink-0" style={{backgroundImage: s.image_url ? `url(${s.image_url})` : "none"}}/>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{s.title}</div>
                      <div className="text-[10px] text-slate-500 uppercase">{s.category}</div>
                      <div className="mt-1 flex gap-2 items-center text-xs">
                        <label className="flex items-center gap-1"><input type="checkbox" checked={s.visible} onChange={e => toggleShowcase(s.showcase_id, "visible", e.target.checked)}/>Visible</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={s.featured} onChange={e => toggleShowcase(s.showcase_id, "featured", e.target.checked)}/>Featured</label>
                        <button onClick={() => delShowcase(s.showcase_id)} className="text-rose-500 ml-auto"><Trash2 className="w-3 h-3"/></button>
                      </div>
                    </div>
                  </div>
                ))}
                {showcase.length === 0 && <div className="text-slate-400 text-sm">No showcase items yet.</div>}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="plans">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((p, i) => (
                <div key={p.plan_id} className="p-4 bg-white rounded-xl border space-y-2">
                  <div className="text-xs font-black uppercase text-sky-500">{p.plan_id}</div>
                  <Input value={p.name} onChange={e => { const c = [...plans]; c[i] = { ...p, name: e.target.value }; setPlans(c); }}/>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">৳ BDT</Label><Input type="number" value={p.price_bdt} onChange={e => { const c = [...plans]; c[i] = { ...p, price_bdt: parseFloat(e.target.value) || 0 }; setPlans(c); }}/></div>
                    <div><Label className="text-xs">$ USD</Label><Input type="number" value={p.price_usd} onChange={e => { const c = [...plans]; c[i] = { ...p, price_usd: parseFloat(e.target.value) || 0 }; setPlans(c); }}/></div>
                  </div>
                  <div><Label className="text-xs">Websites limit</Label><Input type="number" value={p.websites} onChange={e => { const c = [...plans]; c[i] = { ...p, websites: parseInt(e.target.value) || 0 }; setPlans(c); }}/></div>
                  <Button size="sm" onClick={() => savePlan(p)} className="w-full bg-sky-500 hover:bg-sky-600" data-testid={`save-plan-${p.plan_id}`}>Save</Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="tenants">
            <div className="bg-white rounded-xl border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Slug</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Owner</th></tr>
                </thead>
                <tbody>
                  {websites.map(w => (
                    <tr key={w.website_id} className="border-t">
                      <td className="px-4 py-3 font-semibold">{w.name}</td>
                      <td className="px-4 py-3"><span className="text-xs uppercase font-bold text-sky-600">{w.type}</span></td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{w.slug}</td>
                      <td className="px-4 py-3 text-xs">{w.status}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{w.owner_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="affiliates">
            <div className="bg-white rounded-xl border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr><th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Clicks</th><th className="text-left px-4 py-3">Signups</th><th className="text-left px-4 py-3">Pending ৳</th><th className="text-left px-4 py-3">Approved ৳</th></tr>
                </thead>
                <tbody>
                  {affiliates.map(a => (
                    <tr key={a.affiliate_id} className="border-t">
                      <td className="px-4 py-3 font-mono font-bold">{a.code}</td>
                      <td className="px-4 py-3">{a.email}</td>
                      <td className="px-4 py-3">{a.clicks}</td>
                      <td className="px-4 py-3">{a.signups}</td>
                      <td className="px-4 py-3 font-mono">৳{a.pending_commission}</td>
                      <td className="px-4 py-3 font-mono">৳{a.approved_commission}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="withdrawals">
            <div className="bg-white rounded-xl border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr><th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">Amount</th><th className="text-left px-4 py-3">Method</th><th className="text-left px-4 py-3">Account</th><th className="text-left px-4 py-3">Status</th></tr>
                </thead>
                <tbody>
                  {withdrawals.map(w => (
                    <tr key={w.withdrawal_id} className="border-t">
                      <td className="px-4 py-3 font-mono text-xs">{w.user_id}</td>
                      <td className="px-4 py-3 font-mono font-bold">৳{w.amount}</td>
                      <td className="px-4 py-3 uppercase text-xs">{w.method}</td>
                      <td className="px-4 py-3">{w.account}</td>
                      <td className="px-4 py-3"><span className="text-xs uppercase font-bold">{w.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="tickets">
            <div className="space-y-2">
              {tickets.map(t => (
                <div key={t.ticket_id} className="p-4 bg-white rounded-xl border">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      <div className="font-semibold">{t.subject}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{t.email}</div>
                      <div className="text-sm text-slate-700 mt-2">{t.message}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {["open", "in_progress", "closed"].map(s => (
                        <button key={s} onClick={() => setTicket(t.ticket_id, s)} className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${t.status === s ? "bg-sky-500 text-white" : "bg-slate-100 hover:bg-slate-200"}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="payments">
            <div className="bg-white rounded-xl border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr><th className="text-left px-4 py-3">Provider</th><th className="text-left px-4 py-3">Amount</th><th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">TxID / Session</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Action</th></tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.session_id} className="border-t" data-testid={`payment-${p.session_id}`}>
                      <td className="px-4 py-3">
                        <span className="uppercase text-xs font-bold">{p.provider}</span>
                        {p.network && <div className="text-[10px] text-slate-500">{p.network}</div>}
                      </td>
                      <td className="px-4 py-3 font-mono font-bold">{p.currency === "BDT" ? "৳" : "$"}{p.amount}</td>
                      <td className="px-4 py-3 font-mono text-xs">{p.user_id}</td>
                      <td className="px-4 py-3 font-mono text-[11px] break-all max-w-xs">{p.bd_txid || p.crypto_tx_hash || p.session_id}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-bold uppercase px-2 py-1 rounded ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : p.payment_status === "failed" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{p.payment_status}</span></td>
                      <td className="px-4 py-3 text-right">
                        {p.payment_status !== "paid" && (
                          <Button size="sm" onClick={() => verifyPayment(p.session_id, true)} className="bg-emerald-500 hover:bg-emerald-600 h-7 text-xs mr-1" data-testid={`verify-${p.session_id}`}>Approve</Button>
                        )}
                        {p.payment_status !== "failed" && (
                          <Button size="sm" variant="outline" onClick={() => verifyPayment(p.session_id, false)} className="h-7 text-xs text-rose-600 border-rose-200">Reject</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {payments.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400">No payments yet</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="crypto">
            {oxySettings && (
              <div className="max-w-3xl bg-white p-6 rounded-xl border space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg">Crypto Wallet Addresses</h3>
                  <Button size="sm" onClick={addWallet} variant="outline" data-testid="add-wallet">+ Add wallet</Button>
                </div>
                <p className="text-xs text-slate-500">Add wallet addresses for each network. Users will see these when paying for demo unlock. Verification is manual — approve payments from the Payments tab.</p>
                <div className="space-y-3">
                  {(oxySettings.crypto_wallets || []).map((w, i) => (
                    <div key={i} className="p-3 rounded-lg border bg-slate-50 grid sm:grid-cols-4 gap-2" data-testid={`wallet-${i}`}>
                      <div><Label className="text-xs">Network</Label><Input value={w.network} onChange={e => setWallet(i, { network: e.target.value })} placeholder="USDT-TRC20"/></div>
                      <div className="sm:col-span-2"><Label className="text-xs">Address</Label><Input value={w.address} onChange={e => setWallet(i, { address: e.target.value })} placeholder="TXxx... or 0xxx..." className="font-mono text-xs"/></div>
                      <div><Label className="text-xs">Note</Label><div className="flex gap-1"><Input value={w.note || ""} onChange={e => setWallet(i, { note: e.target.value })} placeholder="Low fee"/><Button size="sm" variant="outline" onClick={() => delWallet(i)} className="text-rose-500 border-rose-200"><Trash2 className="w-3 h-3"/></Button></div></div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-100">
                  <div><div className="font-semibold text-sm">Crypto payments enabled</div></div>
                  <Switch checked={oxySettings.crypto_enabled !== false} onCheckedChange={v => setOxySettings({...oxySettings, crypto_enabled: v})}/>
                </div>
                <Button onClick={saveOxy} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold" data-testid="save-crypto">Save Crypto Settings</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="ecom">
            {ecomSettings && (
              <div className="max-w-2xl bg-white p-6 rounded-xl border space-y-3">
                <h3 className="font-bold">Legacy E-commerce Settings</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Commission %</Label><Input type="number" value={ecomSettings.commission_rate} onChange={e => setEcomSettings({...ecomSettings, commission_rate: parseFloat(e.target.value) || 0})}/></div>
                  <div><Label>Delivery Dhaka ৳</Label><Input type="number" value={ecomSettings.delivery_dhaka} onChange={e => setEcomSettings({...ecomSettings, delivery_dhaka: parseFloat(e.target.value) || 0})}/></div>
                  <div><Label>Delivery Outside ৳</Label><Input type="number" value={ecomSettings.delivery_outside} onChange={e => setEcomSettings({...ecomSettings, delivery_outside: parseFloat(e.target.value) || 0})}/></div>
                  <div><Label>bKash number</Label><Input value={ecomSettings.bkash_number} onChange={e => setEcomSettings({...ecomSettings, bkash_number: e.target.value})}/></div>
                  <div><Label>Nagad number</Label><Input value={ecomSettings.nagad_number} onChange={e => setEcomSettings({...ecomSettings, nagad_number: e.target.value})}/></div>
                </div>
                <Button onClick={saveEcom} className="bg-sky-500 hover:bg-sky-600">Save</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PendingBadge({ label, count, color, testid }) {
  const styles = {
    rose: count > 0 ? "bg-rose-500 text-white animate-pulse ring-4 ring-rose-200" : "bg-slate-100 text-slate-500",
    amber: count > 0 ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-500",
    sky: count > 0 ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-500",
    violet: count > 0 ? "bg-violet-500 text-white" : "bg-slate-100 text-slate-500",
  };
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${styles[color] || styles.sky}`} data-testid={testid}>
      <Bell className="w-3 h-3"/>
      <span>{label}</span>
      <span className={`min-w-[22px] h-5 rounded-full flex items-center justify-center font-mono text-xs ${count > 0 ? "bg-white/20" : "bg-slate-200 text-slate-600"}`}>{count}</span>
    </div>
  );
}
